// src/Dashboard/pages/teacher/SuiviCompetitions/components/CompetitionEventCard.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
  FaMinus,
  FaPlus,
  FaUsers,
} from "react-icons/fa";

import {
  formatDateLongFR,
  getEventCategories,
  getEventStatusMeta,
  getAdherentCompetitionCategories,
  getBirthYear,
  uniqSorted,
} from "../utils/suiviCompetitionsUtils";

import CompetitionRegisteredAdherentCard from "./CompetitionRegisteredAdherentCard";
import CompetitionRegisteredAdherentsFilters from "./CompetitionRegisteredAdherentsFilters";

export default function CompetitionEventCard({
  event,
  enseignants = [],
  count,
  expanded,
  registrants = [],
  loadingRegistrants,
  onToggle,
  onRegister,
  onUnregister,
  onOpenResult,
}) {
  const status = getEventStatusMeta(event);
  const categories = getEventCategories(event);
  const hasRegistrants = Number(count || 0) > 0;

  const [registeredFilters, setRegisteredFilters] = useState({
    ageCategory: "",
    birthYear: "",
  });

  useEffect(() => {
    setRegisteredFilters({
      ageCategory: "",
      birthYear: "",
    });
  }, [event?.id]);

  const registeredAgeCategories = useMemo(() => {
    return uniqSorted(
      (registrants || [])
        .map((adherent) => {
          const { ageCategory } = getAdherentCompetitionCategories(adherent);
          return ageCategory;
        })
        .filter(Boolean)
    );
  }, [registrants]);

  const registeredBirthYears = useMemo(() => {
    return uniqSorted(
      (registrants || [])
        .map((adherent) => getBirthYear(adherent?.date_naissance))
        .filter(Boolean)
    );
  }, [registrants]);

  const filteredRegistrants = useMemo(() => {
    return (registrants || []).filter((adherent) => {
      const { ageCategory } = getAdherentCompetitionCategories(adherent);
      const birthYear = getBirthYear(adherent?.date_naissance);

      const matchesAgeCategory =
        !registeredFilters.ageCategory ||
        ageCategory === registeredFilters.ageCategory;

      const matchesBirthYear =
        !registeredFilters.birthYear ||
        String(birthYear) === String(registeredFilters.birthYear);

      return matchesAgeCategory && matchesBirthYear;
    });
  }, [registrants, registeredFilters]);

  return (
    <article className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${status.className}`}
              >
                {status.label}
              </span>

              {event?.type_event && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                  {event.type_event}
                </span>
              )}

              {event?.level_event && (
                <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60">
                  {event.level_event}
                </span>
              )}
            </div>

            <h3 className="mt-3 text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {event?.titre || "Événement"}
            </h3>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="inline-flex items-center gap-2">
                <FaCalendarAlt className="text-slate-400" />
                {formatDateLongFR(event?.start)}
              </span>

              {event?.lieu_event && (
                <span className="text-slate-400">· {event.lieu_event}</span>
              )}
            </div>

            {categories.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((category) => (
                  <span
                    key={category}
                    className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/50"
                  >
                    {category}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <span
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold ring-1 ${
                hasRegistrants
                  ? "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/20 dark:text-red-200 dark:ring-red-800/60"
                  : "bg-slate-50 text-slate-600 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700"
              }`}
            >
              <FaUsers />
              {typeof count === "number"
                ? `${count} inscrit${count > 1 ? "s" : ""}`
                : "…"}
            </span>

            <button
              type="button"
              onClick={onToggle}
              className="inline-flex min-h-[46px] items-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              {expanded ? <FaChevronUp /> : <FaChevronDown />}
              {expanded ? "Replier" : "Voir inscrits"}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-5 border-t border-slate-200 pt-5 dark:border-slate-800">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onRegister}
                className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
              >
                <FaPlus />
                Inscrire
              </button>

              {registrants.length > 0 && (
                <button
                  type="button"
                  onClick={onUnregister}
                  className="inline-flex items-center gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-900/20 dark:text-red-200 dark:ring-red-800/60"
                >
                  <FaMinus />
                  Désinscrire
                </button>
              )}

              <div className="inline-flex items-center gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700">
                <FaInfoCircle />
                Cliquez sur la photo d’un inscrit pour saisir ses résultats.
              </div>
            </div>

            {loadingRegistrants ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {[...Array(8)].map((_, index) => (
                  <div
                    key={index}
                    className="h-[260px] animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : registrants.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
                Aucun inscrit pour le moment.
              </div>
            ) : (
              <>
                <CompetitionRegisteredAdherentsFilters
                  filters={registeredFilters}
                  setFilters={setRegisteredFilters}
                  ageCategories={registeredAgeCategories}
                  birthYears={registeredBirthYears}
                  total={registrants.length}
                  filteredCount={filteredRegistrants.length}
                />

                {filteredRegistrants.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
                    Aucun inscrit ne correspond à ces filtres.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {filteredRegistrants.map((adherent) => (
                      <CompetitionRegisteredAdherentCard
                        key={adherent.id}
                        adherent={adherent}
                        enseignants={enseignants}
                        onClick={() => onOpenResult(adherent)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </article>
  );
}